export type PermitStatus = 'required' | 'special'

export interface CityPermitInfo {
	city: string
	status: PermitStatus
	details: string
	contact: string
	website?: string
}

export const GENERAL_PERMIT_NOTES = [
	'Apply for your permit at least 5–7 business days before delivery — some cities take 24 hours, others take longer.',
	'Dumpsters must not block fire hydrants, loading zones, bus stops, drainage inlets, or parking meters.',
	'Most cities require dumpsters to be placed at least 20 feet from intersections or corners.',
	'Some rental companies (e.g., Frontier Waste) handle the permit process on your behalf — confirm with your hauler.',
	'HOA rules are separate from city permits — check with your HOA even if the city doesn\'t require a permit.',
	'Permit fees are typically $10–$75 depending on city, location, and duration of placement.',
] as const

export const DFW_CITY_PERMITS: CityPermitInfo[] = [
	{
		city: 'Aledo',
		status: 'required',
		details:
			'No permit needed for private property. For street or sidewalk placement, a Right-of-Way permit is required from the City of Aledo Public Works. Small city — expect fast processing.',
		contact: 'City Hall: (817) 441-7108',
	},
	{
		city: 'Allen',
		status: 'required',
		details:
			"Private property placement requires no permit. Street or right-of-way placement requires a ROW permit from Allen's Engineering & Public Works Department.",
		contact: 'Public Works: (214) 509-4700',
	},
	{
		city: 'Arlington',
		status: 'required',
		details:
			"No permit required for private property. A permit is required for any dumpster placed on a public street, sidewalk, or right-of-way. Contact the City of Arlington Public Works / Development Services. Apply via the City's online permitting portal.",
		contact: 'Public Works: (817) 459-6777',
		website: 'arlingtontx.gov',
	},
	{
		city: 'Azle',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, a permit is required from the City of Azle. Contact the Public Works or Building Department.',
		contact: 'City of Azle: (817) 444-7003',
	},
	{
		city: 'Balch Springs',
		status: 'required',
		details:
			'Standard Texas rules apply. No permit needed on private property. Placement on public streets or sidewalks requires a ROW permit from the City of Balch Springs.',
		contact: 'City Hall: (972) 557-6000',
	},
	{
		city: 'Bedford',
		status: 'required',
		details:
			"Private property placement is permit-free. Street or sidewalk placement requires a Right-of-Way permit from Bedford's Public Works Department.",
		contact: 'Public Works: (817) 952-2200',
	},
	{
		city: 'Benbrook',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, contact the City of Benbrook for a temporary right-of-way permit.',
		contact: 'City of Benbrook: (817) 249-3000',
	},
	{
		city: 'Burleson',
		status: 'required',
		details:
			'No permit required on private property (driveways, job sites). For street or public ROW placement, a permit is required from the City of Burleson Public Works. Dumpsters cannot be placed on grass or circular driveways.',
		contact: 'Public Works: (817) 426-9600',
	},
	{
		city: 'Carrollton',
		status: 'required',
		details:
			'Private property placement is permit-free. For placement on streets, sidewalks, or alleys, a Right-of-Way permit is required. City ordinance prohibits blocking or impeding a street or alley right-of-way.',
		contact: 'Public Works: (972) 466-3012',
	},
	{
		city: 'Cedar Hill',
		status: 'required',
		details:
			'No permit needed for private property. For placement on public streets or sidewalks, contact the City of Cedar Hill Public Works for a ROW or temporary use permit.',
		contact: 'City Hall: (972) 291-5100',
	},
	{
		city: 'Cleburne',
		status: 'required',
		details:
			'No permit needed on private property. Street or ROW placement requires a permit from the City of Cleburne. Check with the Public Works or Building department.',
		contact: 'City of Cleburne: (817) 645-0975',
	},
	{
		city: 'Colleyville',
		status: 'required',
		details:
			"A Residential Right-of-Way Permit from Colleyville's Public Works Department is required for any street placement. No permit needed on private property such as your driveway or a job site. Flat, paved surface required regardless of placement.",
		contact: 'Public Works: (817) 503-1200',
		website: 'colleyville.com',
	},
	{
		city: 'Coppell',
		status: 'required',
		details:
			'Private property placement is permit-free. Street or right-of-way placement requires a ROW permit from the City of Coppell Engineering/Public Works.',
		contact: 'City of Coppell: (972) 462-0022',
	},
	{
		city: 'Corinth',
		status: 'required',
		details:
			'No permit needed on private property. For placement on public streets or sidewalks, a ROW permit is required from the City of Corinth.',
		contact: 'City of Corinth: (940) 321-8100',
	},
	{
		city: 'Dallas',
		status: 'required',
		details:
			'A Right-of-Way (ROW) permit from the Dallas Department of Public Works is required for any dumpster placed on a public street, sidewalk, or alley. Fees range from $25–$75 depending on duration and location. Downtown and historic districts may have additional restrictions. Online application available. No permit needed on private property.',
		contact: 'Dept of Public Works: (214) 948-4200',
		website: 'dallascityhall.com',
	},
	{
		city: 'Denton',
		status: 'required',
		details:
			'ROW permits are processed online through e-TRAKiT (as of January 1, 2022). All contractors must register with the Public Works Inspections Division before applying. No permit needed on private property. Visit the Development Service Center at 401 N. Elm St. for in-person help.',
		contact: 'Public Works Inspections: (940) 349-8910',
		website: 'cityofdenton.com',
	},
	{
		city: 'DeSoto',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, a permit is required from the City of DeSoto. Contact Public Works or the Building Department.',
		contact: 'City of DeSoto: (972) 230-9600',
	},
	{
		city: 'Duncanville',
		status: 'required',
		details:
			'Private property placement is permit-free. Street or sidewalk placement requires a ROW permit from the City of Duncanville.',
		contact: 'City Hall: (972) 780-5000',
	},
	{
		city: 'Ennis',
		status: 'required',
		details:
			'No permit needed on private property. For public street placement, contact the City of Ennis for a temporary right-of-way permit.',
		contact: 'City of Ennis: (972) 878-1234',
	},
	{
		city: 'Euless',
		status: 'required',
		details:
			'Private property placement is permit-free. For street or sidewalk placement in Euless, a ROW permit from Public Works is required.',
		contact: 'City of Euless: (817) 685-1400',
	},
	{
		city: 'Farmers Branch',
		status: 'required',
		details:
			'No permit required for private property. For placement on public streets or rights-of-way, a permit is required from the City of Farmers Branch.',
		contact: 'City of Farmers Branch: (972) 919-2500',
	},
	{
		city: 'Flower Mound',
		status: 'required',
		details:
			'No permit needed on private property. For street or public ROW placement, a permit is required from the Town of Flower Mound Engineering/Public Works.',
		contact: 'Public Works: (972) 874-6300',
	},
	{
		city: 'Forney',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, contact the City of Forney Public Works for a temporary placement permit.',
		contact: 'City of Forney: (972) 564-7336',
	},
	{
		city: 'Fort Worth',
		status: 'required',
		details:
			'Any dumpster placed on a public street or sidewalk requires a permit from the City of Fort Worth Public Works Department. Online application available. Fees for short-term rentals are generally under $50. Permits specify placement conditions and duration limits.',
		contact: 'Public Works: (817) 392-8100',
		website: 'fortworthtexas.gov',
	},
	{
		city: 'Frisco',
		status: 'required',
		details:
			'Private property placement is permit-free. For placement on public streets, sidewalks, or alleys, a Right-of-Way permit is required from the City of Frisco Engineering/Public Works.',
		contact: 'Engineering Dept: (972) 292-5400',
	},
	{
		city: 'Garland',
		status: 'required',
		details:
			"All users working in Garland's rights-of-way must register with the City of Garland Engineering Department and obtain a ROW permit before placing any equipment including dumpsters in a public ROW. Visit the Engineering Dept at 800 Main Street (2nd floor, Ronald Jones Municipal Building). No permit needed on private property.",
		contact: 'Engineering Dept: (972) 205-2170',
		website: 'garlandtx.gov',
	},
	{
		city: 'Glenn Heights',
		status: 'required',
		details:
			'Standard Texas ROW rules apply. No permit needed on private property. Public street or sidewalk placement requires a city permit. Contact City Hall.',
		contact: 'City Hall: (972) 223-1690',
	},
	{
		city: 'Grand Prairie',
		status: 'required',
		details:
			'No permit required on private property. For public street or sidewalk placement, a Right-of-Way permit is required from the City of Grand Prairie Public Works or Engineering.',
		contact: 'Public Works: (972) 237-8050',
	},
	{
		city: 'Grapevine',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement in Grapevine, a permit is required from the Public Works Department.',
		contact: 'Public Works: (817) 410-3491',
	},
	{
		city: 'Haltom City',
		status: 'required',
		details:
			'Standard rules apply. No permit on private property; permit required for public streets or sidewalks. Contact the City of Haltom City Public Works.',
		contact: 'City Hall: (817) 222-7700',
	},
	{
		city: 'Hurst',
		status: 'required',
		details:
			"Private property placement is permit-free. For street or ROW placement, a permit is required from Hurst's Public Works Department.",
		contact: 'Public Works: (817) 788-7025',
	},
	{
		city: 'Irving',
		status: 'required',
		details:
			'No permit required on private property. For public street or sidewalk placement, a Right-of-Way permit is required from the City of Irving. Contact the Transportation & Public Works Department.',
		contact: 'Public Works: (972) 721-2631',
	},
	{
		city: 'Keller',
		status: 'required',
		details:
			'No permit needed on private property. For street or public ROW placement in Keller, a permit is required from the Public Works or Engineering Department.',
		contact: 'City of Keller: (817) 743-4000',
	},
	{
		city: 'Lancaster',
		status: 'required',
		details:
			'No permit required on private property. For public street or sidewalk placement, contact the City of Lancaster for a ROW permit.',
		contact: 'City of Lancaster: (972) 218-1300',
	},
	{
		city: 'Lewisville',
		status: 'required',
		details:
			'Private property placement is permit-free. Street or sidewalk placement requires a ROW permit from the City of Lewisville Engineering/Public Works.',
		contact: 'Public Works: (972) 219-3700',
	},
	{
		city: 'Little Elm',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, a permit is required from the Town of Little Elm.',
		contact: 'City Hall: (214) 975-0460',
	},
	{
		city: 'Mansfield',
		status: 'special',
		details:
			'Per City Ordinance Chapters 50.50 & 50.60, waste haulers must hold a franchise agreement with the City of Mansfield to operate within city limits. Frontier Waste Solutions is the franchised hauler. A permit is also required for any ROW or public street placement — contact the Mansfield Building Safety Department. Confirm your hauler\'s franchise status before booking.',
		contact: 'Building Safety: (817) 276-4220',
		website: 'mansfieldtexas.gov',
	},
	{
		city: 'McKinney',
		status: 'required',
		details:
			'No permit required on private property. For public street or ROW placement, a permit is required. Private haulers must hold a license agreement with the City of McKinney to collect/transport commercial and construction recyclable materials. Contact McKinney Solid Waste Services to confirm your hauler\'s status.',
		contact: 'Solid Waste Services: (972) 547-7385',
		website: 'mckinneytexas.org',
	},
	{
		city: 'Mesquite',
		status: 'required',
		details:
			'No permit needed on private property. For placement on city streets, sidewalks, or rights-of-way, the City of Mesquite may require a temporary-use permit. Requirements vary based on neighborhood, traffic patterns, and location. Check with the city or your HOA before scheduling.',
		contact: 'City of Mesquite: (972) 216-6200',
	},
	{
		city: 'Midlothian',
		status: 'required',
		details:
			'No permit required on private property or construction sites. Street or public ROW placement requires a permit from the City of Midlothian.',
		contact: 'City Hall: (972) 775-7131',
	},
	{
		city: 'Murphy',
		status: 'required',
		details:
			'No permit needed on private property. For street or sidewalk placement, a permit is required from the City of Murphy.',
		contact: 'City of Murphy: (972) 468-4200',
	},
	{
		city: 'North Richland Hills',
		status: 'required',
		details:
			'Private property placement is permit-free. For public street or sidewalk placement, a ROW permit is required from the City of North Richland Hills Public Works.',
		contact: 'Public Works: (817) 427-6400',
	},
	{
		city: 'Plano',
		status: 'required',
		details:
			'A ROW permit from the City of Plano Engineering Department is required for placement on any public street, sidewalk, or alley. No permit needed if placed on private property (driveway, lot). Obtain the permit before delivery.',
		contact: 'Engineering Dept: (972) 941-7151',
		website: 'plano.gov',
	},
	{
		city: 'Prosper',
		status: 'required',
		details:
			'Standard rules apply. No permit needed on private property. For public street or ROW placement, contact the Town of Prosper Engineering/Public Works for a permit.',
		contact: 'Town of Prosper: (972) 347-9900',
	},
	{
		city: 'Richardson',
		status: 'required',
		details:
			'No permit required on private property. Street or public ROW placement requires a ROW permit from the City of Richardson Engineering/Public Works.',
		contact: 'Public Works: (972) 744-4100',
	},
	{
		city: 'Rockwall',
		status: 'required',
		details:
			'No permit needed on private property. For street or sidewalk placement, a permit is required from the City of Rockwall.',
		contact: 'City of Rockwall: (972) 771-7700',
	},
	{
		city: 'Rowlett',
		status: 'required',
		details:
			'Private property placement is permit-free. For public street or ROW placement, a permit is required from the City of Rowlett.',
		contact: 'City of Rowlett: (972) 412-6100',
	},
	{
		city: 'Sachse',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, a permit is required from the City of Sachse.',
		contact: 'City of Sachse: (972) 495-1212',
	},
	{
		city: 'Southlake',
		status: 'required',
		details:
			'No permit required on private property (driveways, job sites). For placement on a public street or right-of-way in Southlake, the city may require a temporary permit. Contact the Public Works Department before scheduling delivery.',
		contact: 'Public Works: (817) 748-8082',
	},
	{
		city: 'The Colony',
		status: 'required',
		details:
			'No permit needed on private property. For public street or sidewalk placement, a ROW permit is required from the City of The Colony.',
		contact: 'City Hall: (972) 625-1106',
	},
	{
		city: 'Trophy Club',
		status: 'required',
		details:
			'Standard ROW rules apply. No permit needed on private property. For public street or ROW placement, contact the Town of Trophy Club.',
		contact: 'Town of Trophy Club: (682) 831-4600',
	},
	{
		city: 'University Park',
		status: 'required',
		details:
			'No permit required on private property. For street or alley placement in University Park, a permit is required from the city. Residential streets in UP are narrow — verify clearance requirements.',
		contact: 'City Hall: (214) 363-1644',
	},
	{
		city: 'Waxahachie',
		status: 'required',
		details:
			'No permit needed on private property. For public street or ROW placement, contact the City of Waxahachie Public Works for a permit.',
		contact: 'Public Works: (972) 937-7330',
	},
	{
		city: 'Weatherford',
		status: 'required',
		details:
			'No permit required on private property. For street or public ROW placement, a permit is required from the City of Weatherford.',
		contact: 'City of Weatherford: (817) 598-4200',
	},
	{
		city: 'White Settlement',
		status: 'required',
		details:
			'No permit needed on private property. Street or ROW placement requires a permit from the City of White Settlement.',
		contact: 'City Hall: (817) 246-4971',
	},
	{
		city: 'Wylie',
		status: 'required',
		details:
			'Private property placement is permit-free. For public street or sidewalk placement, a ROW permit is required from the City of Wylie.',
		contact: 'City of Wylie: (972) 516-6000',
	},
]
